import React, { useState } from "react";
import "./ItemGroup.css";
import { CiVideoOn } from "react-icons/ci";
import {
  Button,
  ButtonGroup,
  Stack,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  ListItemButton,
  Badge,
} from "@mui/material";
import okIcon from "assets/OkIcon.svg";
import pendingIcon from "assets/PendingIcon.svg";
import issueIcon from "assets/IssueIcon.png";
import limboIcon from "assets/LimboIcon.jpg";
import crossIcon from "assets/CrossIcon.svg";
import { CaretRightOutlined } from "@ant-design/icons";

interface Item {
  id: number;
  title: {id:string , value:string};
  status: "OK" | "Pending" | "Issue" | "Limbo";
  type: "video" | "document" | "image";
  category: string;
}

interface ItemGroupProps {
  items: Item[];
  onItemSelect: (id: number) => void;
}

const ItemGroup: React.FC<ItemGroupProps> = ({ items, onItemSelect }) => {
  const [view, setView] = useState<"grid" | "list" | "table">("grid");
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  const handleItemClick = (id: number) => {
    setSelectedItemId(id);
    onItemSelect(id);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "OK":
        return okIcon;
      case "Pending":
        return pendingIcon;
      case "Issue":
        return issueIcon;
      case "Limbo":
        return limboIcon;
      default:
        return crossIcon;
    }
  };

  return (
    <div className="item-group">
      <div className="item-group-heading">
        <div className="item-group-title">
          <h1>1.2 Solids</h1>
        </div>
        <div className="group-list">
          <ButtonGroup
            variant="contained"
            color="primary"
            className="view-selector"
          >
            <Button
              onClick={() => setView("grid")}
              variant={view === "grid" ? "contained" : "outlined"}
            >
              Grid
            </Button>
            <Button
              onClick={() => setView("list")}
              variant={view === "list" ? "contained" : "outlined"}
            >
              List
            </Button>
            <Button
              onClick={() => setView("table")}
              variant={view === "table" ? "contained" : "outlined"}
            >
              Table
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div className={`items-view ${view}`}>
        {view === "table" ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Optional/Compulsory</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    onClick={() => handleItemClick(item.id)}
                    selected={selectedItemId === item.id}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      {item.title.id}{item.title.value}
                    </TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>
                       {item.status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : view === "list" ? (
          <List>
            {items.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  onClick={() => handleItemClick(item.id)}
                  selected={selectedItemId === item.id}
                  className={`list-item ${
                    selectedItemId === item.id ? "selected" : ""
                  }`}
                >
                  <Typography variant="h6">{item.title.id} {item.title.value}</Typography>
                  <CaretRightOutlined />{" "}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {items.map((item) => (
              <Stack
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={`grid-item ${
                  selectedItemId === item.id ? "selected" : ""
                }`}
                sx={{
                  border: "1px solid #000",
                  borderRadius: "8px",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  padding: "10px",
                  textAlign: "center",
                  backgroundColor: "#f9f9f9",
                  cursor: "pointer",
                  width: { xs: "100%", sm: "48%", md: "23%" },
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.3s, border-color 0.3s",
                  "&.selected": {
                    backgroundColor: "#d0eaff",
                    borderColor: "#007bff",
                  },
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  badgeContent={
                    <img
                      src={getStatusIcon(item.status)}
                      alt={item.status}
                      style={{ width: 16, height: 16 }}
                    />
                  }
                >
                  <CiVideoOn className="icon" />
                </Badge>
                <Typography variant="h6">{item.title.id}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </div>
    </div>
  );
};

export default ItemGroup;
